import React, { useCallback, useEffect, useState } from 'react';
import {
  Flex,
  Spinner,
  StackDivider,
  VStack,
  useDisclosure,
  Heading,
  Text
} from '@chakra-ui/react';
import ContentLayout from '@layouts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import {
  getFastTransferConfig,
  updateFastTransferConfig
} from '@store/actions/fastTransferManagerActions';
import { updateFastTransferManagerReqData } from '@store/actions/reqDataActions';
import { FAST_TRANSFER_MANAGER_STRINGS } from './constant';
import { fastTransferConfig as config, diffRails } from './utils';
import {
  PermissionDenied,
  FastTransferTable,
  ConfirmUpdateModal
} from '@components/FastTransferManager';

const FastTransferManager = () => {
  const { hasAnyAuthority } = useAuthRole();
  const dispatch = useDispatch();

  const reqData = useSelector((state) => state.reqData.fastTransferManager);
  const {
    data: rawConfigData,
    isLoading,
    isError,
    error
  } = useSelector((state) => state.fastTransferManager.fastTransferConfig);

  // Local State
  const [configToEdit, setConfigToEdit] = useState(null);
  const [formData, setFormData] = useState({});
  const [isModalLoading, setIsModalLoading] = useState(false);

  const {
    isOpen: isConfirmUpdateModalOpen,
    onOpen: onConfirmUpdateModalOpen,
    onClose: onConfirmUpdateModalClose
  } = useDisclosure();

  // Convert raw data to entity format
  const configs = rawConfigData?.countries
    ? config.entityConvertor(rawConfigData.countries)
    : [];

  useEffect(() => {
    fetchFastTransferConfig();
  }, []);

  useEffect(() => {
    if (configToEdit) {
      setFormData(config.entityToFormData(configToEdit));
    }
  }, [configToEdit]);

  const fetchFastTransferConfig = useCallback(() => {
    dispatch(
      getFastTransferConfig({
        onSuccess: () => {
          dispatch(
            updateFastTransferManagerReqData({
              ...reqData,
              totalPages: 1
            })
          );
        }
      })
    );
  }, [dispatch, reqData]);

  const handleEditMode = useCallback((e, configEntity) => {
    e.preventDefault();
    setConfigToEdit(configEntity);
  }, []);

  const handleEditCleanUp = useCallback(() => {
    setFormData({});
    setConfigToEdit(null);
    onConfirmUpdateModalClose();
  }, [onConfirmUpdateModalClose]);

  const handleToggleChange = (railName, checked) => {
    setFormData((prev) => ({
      ...prev,
      enabledRails: {
        ...prev.enabledRails,
        [railName]: { isEnabled: checked },
      },
    }));
  };

  const handleUpdateConfigCancellation = useCallback(() => {
    handleEditCleanUp();
  }, [handleEditCleanUp]);

  const getConfirmationMessage = useCallback(() => {
    if (!configToEdit)
      return FAST_TRANSFER_MANAGER_STRINGS.CONFIRM_UPDATE_MESSAGE;

    const countryName =
      configToEdit.data.find((d) => d.name === 'countryCode')?.displayValue ||
      configToEdit.id;

    const railsField = configToEdit.data.find((d) => d.name === 'rails');
    const changes = diffRails(railsField?.value, formData.enabledRails);

    if (changes.length === 0)
      return FAST_TRANSFER_MANAGER_STRINGS.CONFIRM_NO_CHANGES;

    const parts = changes.map(
      (c) => `${c.to ? 'Enable' : 'Disable'} ${c.rail}`
    );
    return `${parts.join(' and ')} for ${countryName}?`;
  }, [configToEdit, formData.enabledRails]);

  const handleSaveConfig = useCallback(() => {
    if (!configToEdit) return;

    setIsModalLoading(true);

    // Get the original country config from raw data to preserve all fields
    const payload = config.payloadConvertor(
      formData,
      configToEdit.id,
      rawConfigData?.countries
    );

    dispatch(
      updateFastTransferConfig({
        payload,
        onSuccess: () => {
          const countryName =
            configToEdit.data.find((d) => d.name === 'countryCode')
              ?.displayValue || configToEdit.id;

          const railsField = configToEdit.data.find(
            (d) => d.name === 'rails'
          );
          const changes = diffRails(
            railsField?.value,
            formData.enabledRails
          );
          const summary =
            changes.length > 0
              ? changes
                  .map(
                    (c) => `${c.rail}: ${c.to ? 'enabled' : 'disabled'}`
                  )
                  .join(', ')
              : 'no changes';

          dispatch({
            type: 'SET_ALERT',
            payload: {
              title: `Fast transfer updated for ${countryName} (${summary})`,
              status: 'success',
            },
          });

          fetchFastTransferConfig();
        },
        onComplete: () => {
          setIsModalLoading(false);
          handleEditCleanUp();
        }
      })
    );
  }, [
    configToEdit,
    formData,
    rawConfigData,
    dispatch,
    fetchFastTransferConfig,
    handleEditCleanUp
  ]);

  const getRowStyles = useCallback(
    (configEntity) => {
      const isSelected = configToEdit?.id === configEntity.id;

      const activeStyles = {
        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.6)',
        backgroundColor: 'black',
        border: '6px solid #1c1c1c'
      };

      const inactiveStyles = {
        boxShadow: 'none',
        backgroundColor: '#1c1c1c',
        border: '3px solid black',
        opacity: configToEdit ? 0.6 : 1,
        filter: configToEdit ? 'blur(1px)' : 'none'
      };

      return {
        ...(isSelected ? activeStyles : inactiveStyles),
        transition: 'all 0.3s ease-in-out',
        _hover: {
          backgroundColor: isSelected ? 'black' : '#111111'
        }
      };
    },
    [configToEdit]
  );

  const hasWriteAccess = hasAnyAuthority(ROLES.FAST_TRANSFERS_WRITE);
  const hasReadAccess = hasAnyAuthority(ROLES.FAST_TRANSFERS_READ);

  const renderContent = () => {
    if (!hasReadAccess) {
      return <PermissionDenied />;
    }

    if (isLoading) {
      return (
        <Flex justifyContent="center" alignItems="center">
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
        </Flex>
      );
    }

    if (isError) {
      return (
        <Flex
          p={4}
          borderRadius="xl"
          bg="backGround"
          justifyContent="center"
          alignItems="center"
        >
          {error || 'Failed to load fast transfer configuration'}
        </Flex>
      );
    }

    return (
      <FastTransferTable
        configs={configs}
        configToEdit={configToEdit}
        formData={formData}
        hasWriteAccess={hasWriteAccess}
        handleToggleChange={handleToggleChange}
        handleEditMode={handleEditMode}
        handleEditCleanUp={handleEditCleanUp}
        onConfirmUpdateModalOpen={onConfirmUpdateModalOpen}
        getRowStyles={getRowStyles}
      />
    );
  };

  return (
    <ContentLayout>
      <ConfirmUpdateModal
        isOpen={isConfirmUpdateModalOpen}
        onClose={onConfirmUpdateModalClose}
        handleSubmit={handleSaveConfig}
        handleCancel={handleUpdateConfigCancellation}
        prompt={getConfirmationMessage()}
        isLoading={isModalLoading}
      />

      <VStack
        overflowY="auto"
        overflowX="hidden"
        h={{ lg: '90vh' }}
        pt={10}
        pb={4}
        px={{ base: 4, sm: 6, lg: 10 }}
        alignItems="stretch"
        gap={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        <Heading size="lg" color="colorPrimary">
          {FAST_TRANSFER_MANAGER_STRINGS.HEADING}
        </Heading>
        <Text fontSize="sm" color="whiteAlpha.700">
          {FAST_TRANSFER_MANAGER_STRINGS.SUBHEADING}
        </Text>
        {renderContent()}
      </VStack>
    </ContentLayout>
  );
};

export default FastTransferManager;
